import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Axios from 'axios';
import Checkbox from '@/shared/components/input/Checkbox';
import { CanCreate } from '@/shared/components/security';
import handleHttpError from '@/shared/services/HandleHttpError';
import ToastrService from '@/shared/services/ToastrService';
import { FormattedMessage, injectIntl } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import ScrollableTable from '@/shared/components/table/ScrollableTable';
import ColumnHeader from '@/shared/components/table/ColumnHeader';
import SimplePanel from '@/shared/components/SimplePanel';
import PopoverStickOnHover from '@/shared/components/PopoverStickOnHover';
import SendEmailModal from '@/shared/components/email/MultiRelationsMailModal';
import KPIs from '@/shared/components/KPIs';
import KPI from '@/shared/components/KPI';
import api from '@/shared/api';
import SignalRService from '@/shared/services/SignalRService';
import { FaFileExcel, FaEnvelope } from 'react-icons/fa';
import { CommentIcon } from '@/shared/components/Icons';
import FileSaver from 'file-saver';

import utils from '@/shared/utils/adminpulse';
import FormattedListValue, {
  listItemMsg,
  formatListValue,
} from '@/shared/components/FormattedListValue';
import { injectStateService, StateService } from '@/shared/services/StateService';
import RelationName from '@/shared/components/RelationName';
import SingleRelationMailModal from '@/shared/components/email/SingleRelationMailModal';
import { EnvelopeIcon } from '@/shared/components/Icons';
import config from '@/config';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import Button from '@/shared/components/Button';
import { CanUpdate } from '@/shared/components/security';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import MarkCompliancyInapplicableModal from './MarkCompliancyInapplicableModal';
import CompliancyStatusToggle from './CompliancyStatusToggle';
import messages from './comliancyMessages';
import CompliancyGlobalActionsButton from './CompliancyGlobalActionsButton';
import getFieldIdForCompliancy from './getFieldIdForCompliancy';
import CompliancyFilters, { defaultRelationFilters } from './CompliancyFilters';
import BatchRegistrationModal from '@/shared/components/registration/BatchRegistrationModal';
import UpdateRelationTagsModal from '@/modules/relations/overview/modals/UpdateRelationTagsModal';

// eslint-disable-next-line
const ComplianceKPISubtitle = ({ item }) => (
  <React.Fragment>
    <div className="kpi-percentage">{item.percentage}%</div>
    <div className="kpi-compliance">
      {item.compliant}/{item.total} <FormattedListValue list="compliancestatus" id={2} />
    </div>
  </React.Fragment>
);

class CompliancyContainer extends Component {
  constructor(props) {
    super(props);

    const { relationFilters: sessionFilters = {}, ...sessionParams } =
      JSON.parse(sessionStorage.getItem('CompliancyParams')) || {};

    const currentSearchParams = {
      ...sessionParams,
      page: 0,
      sortKey: sessionParams.sortKey || 'displayName',
      relationFilters: {
        ...defaultRelationFilters,
        ...sessionFilters,
        amlEndScore: null,
      },
    };
    this.state = {
      complianceKPIs: [],
      fullyCompliantKPI: null,
      complianceTypes: [],
      items: [],
      allSelected: false,
      showSendMailModal: false,
      showMarkComplianceTypeAsInapplicableModal: false,
      showSendSingleRelationMailModal: false,
      currentSearchParams,
      relationLanguages: [],
      initialSearchParams: currentSearchParams,
      showCreateRegistrationModal: false,
      showUpdateRelationTagsModal: false,
    };
  }

  componentDidMount() {
    SignalRService.subscribe('compliancyUpdated', this.compliancyUpdateHandler);
    // this.loadKPIs();
  }

  componentWillUnmount() {
    SignalRService.unsubscribe('compliancyUpdated', this.compliancyUpdateHandler);
    if (this.retrieveToken && !this.retrieveToken.token.reason) {
      this.retrieveToken.cancel('Operation canceled due to leaving page.');
    }
  }

  loadKPIs = () => {
    const { intl } = this.props;
    const { currentSearchParams: searchParams } = this.state;

    const statusFilters = Object.keys(searchParams.filter).reduce((obj, key) => {
      if (key !== 'amlRiskLevel' && key !== 'amlEndScore') {
        obj[key] = searchParams.filter[key].items;
      }
      return obj;
    }, {});

    const amlEndScore =
      searchParams.filter && searchParams.filter.amlEndScore
        ? searchParams.filter.amlEndScore.items || []
        : [];

    const bodyParams = {
      ...searchParams.relationFilters,
      statusFilters: {
        ...searchParams.relationFilters.statusFilters,
        ...statusFilters,
      },
      ...this.getSelectionParams(),
      amlEndScore,
    };

    return Promise.all([
      api.base.post(`/api/compliance/KPI`, bodyParams),
      api.base.post(`/api/compliance/FullyCompliantKPI`, bodyParams),
    ])
      .then(([{ data }, { data: fullyCompliant }]) => {
        this.setState(state => ({
          ...state,
          fullyCompliantKPI: fullyCompliant
            ? {
                percentage: utils.toPercentage(
                  fullyCompliant.compliant,
                  fullyCompliant.total,
                  true,
                ),
                compliant: fullyCompliant.compliant,
                total: fullyCompliant.total,
              }
            : state.fullyCompliantKPI,
          complianceKPIs: data
            ? data
                .map(item => ({
                  type: item.compliancyType,
                  title: intl.formatMessage(listItemMsg('compliancetype', item.compliancyType)),
                  percentage: utils.toPercentage(item.ok, item.total, true),
                  compliant: item.ok,
                  total: item.total,
                }))
                .sort(dynamicSort('title'))
            : state.complianceKPIs,
        }));
      })
      .catch(handleHttpError(intl));
  };

  loadItems = searchParams => {
    const { allSelected } = this.state;
    const { intl } = this.props;
    if (this.retrieveToken && !this.retrieveToken.token.reason) {
      this.retrieveToken.cancel('Operation canceled due to new request.');
    }
    this.retrieveToken = Axios.CancelToken.source();

    const urlParams = {
      desc: searchParams.desc,
      sort: searchParams.sortKey,
      page: searchParams.page,
      take: searchParams.pageSize,
    };

    const statusFilters = Object.keys(searchParams.filter).reduce((obj, key) => {
      if (key !== 'amlRiskLevel' && key !== 'amlEndScore') {
        obj[key] = searchParams.filter[key].items;
      }
      return obj;
    }, {});

    const amlEndScore =
      searchParams.filter && searchParams.filter.amlEndScore
        ? searchParams.filter.amlEndScore.items || []
        : [];

    const bodyParams = {
      ...searchParams.relationFilters,
      statusFilters: {
        ...searchParams.relationFilters.statusFilters,
        ...statusFilters,
      },
      amlEndScore,
    };
    sessionStorage.setItem('CompliancyParams', JSON.stringify(searchParams));

    return api.base
      .post(`/api/compliance/get`, bodyParams, {
        params: {
          ...urlParams,
        },
        cancelToken: this.retrieveToken.token,
      })
      .then(({ data }) => {
        const rows = data.relations.map(e => ({ ...e, selected: !!allSelected }));

        this.setState(
          state => ({
            ...state,
            items: searchParams.page > 0 ? [...state.items, ...rows] : rows,
            total: data.total,
            complianceTypes: data.complianceTypes
              .map(x => ({
                id: x,
                label: intl.formatMessage(listItemMsg('compliancetype', x, x)),
              }))
              .sort(dynamicSort('label'))
              .map(x => x.id),
            complianceKPIs: data.complianceKPIs
              ? data.complianceKPIs.map(item => ({
                  type: item.compliancyType,
                  title: intl.formatMessage(listItemMsg('compliancetype', item.compliancyType)),
                  percentage: utils.toPercentage(item.ok, item.total, true),
                  compliant: item.ok,
                  total: item.total,
                }))
              : state.complianceKPIs,
            currentSearchParams: searchParams,
          }),
          () => {
            if (searchParams.page === 0) {
              this.loadKPIs();
            }
          },
        );

        return data.relations;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
      });
  };

  compliancyUpdateHandler = relationId => {
    if (!relationId) return;
    const { items } = this.state;
    // Check if relation is loaded
    if (!items.some(x => x.id === relationId)) return;

    api.base
      .post(`/api/compliance/get?desc=false&sort=name&page=0&take=50`, { relationId })
      .then(({ data }) => {
        if (!data || !data.relations || data.relations.length !== 1) return;
        const row = data.relations[0];
        this.setState(state => ({
          items: state.items.map(x => {
            if (x.id !== row.id) return x;
            return row;
          }),
        }));
      });
  };

  handleUpdateComplianceStatus = (relationId, complianceType, complianceStatus) => {
    if (typeof complianceType === 'undefined') return;
    this.setState(state => ({
      items: state.items.map(x => {
        if (x.id !== relationId) return x;
        return {
          ...x,
          compliancies: x.compliancies.map(y => {
            if (y.complianceType !== complianceType) return y;
            return { ...y, complianceStatus };
          }),
        };
      }),
    }));
  };

  filterByKpi(type) {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        page: 0,
        relationFilters: {
          ...state.initialSearchParams.relationFilters,
          statusFilters: {
            ...state.initialSearchParams.relationFilters.statusFilters,
            [type]: [0, 1],
          },
        },
      },
    }));
  }

  toggleNotFullyCompliantFilter = () => {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        relationFilters: {
          ...state.initialSearchParams.relationFilters,
          onlyNotFullyCompliant: !state.initialSearchParams.relationFilters.onlyNotFullyCompliant,
        },
      },
    }));
  };

  filtersChanged = filters => {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        relationFilters: filters,
      },
    }));
  };

  resetFilters = () => {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        filter: {},
        search: {},
        page: 0,
        relationFilters: { ...defaultRelationFilters },
      },
    }));
  };

  showCreateRegistrationModal = () => {
    this.setState(state => ({
      ...state,
      showCreateRegistrationModal: true,
    }));
  };

  createBatchRegistrations = batchRegistrationModel =>
    this.executeBatchAction({
      action: 'createregistration',
      parameters: batchRegistrationModel,
    });

  updateRelationTags = data =>
    this.executeBatchAction({
      action: 'tagsupdate',
      parameters: data,
    });

  getCountSelectedRelationsFormatted() {
    const { total } = this.state;
    const { intl } = this.props;
    const count = this.getCountSelectedRelations();

    return intl.formatMessage(messages.CountRelationsSelected, { count, total });
  }

  getCountSelectedRelations() {
    const { allSelected, total, items } = this.state;

    const count = items ? items.filter(x => x.selected !== allSelected).length : 0;

    if (allSelected) {
      return total - count;
    }
    return count;
  }

  iconClicked = (item, compliancyType, compliancyStatus) => {
    const { stateService } = this.props;
    const field = getFieldIdForCompliancy(compliancyType, compliancyStatus, item.type);
    stateService.gotoRelation(item.id, { gotoField: field });
  };

  handleSelectAll = () => {
    const { items, allSelected } = this.state;

    const isCurrentlySelected = !allSelected;

    const itemsCopy = items.map(e => ({ ...e, selected: !!isCurrentlySelected }));

    this.setState(state => ({
      ...state,
      allSelected: isCurrentlySelected,
      items: itemsCopy,
    }));
  };
  handleSelect = relation => {
    this.setState(({ items }) => ({
      items: items.map(x => (x.id === relation.id ? { ...x, selected: !x.selected } : x)),
    }));
  };

  getCountSelectionsFormatted() {
    const { total } = this.state;
    const { intl } = this.props;
    const count = this.getCountSelections();

    return intl.formatMessage(messages.CountRelationsSelected, { count, total });
  }

  getCountSelections() {
    const { allSelected, total, items } = this.state;

    const count = items ? items.filter(x => x.selected !== allSelected).length : 0;

    if (allSelected) {
      return total - count;
    }
    return count;
  }

  isGlobalActionsDisabled = () => {
    const { items, allSelected } = this.state;
    if (!items || !items.length) return true;
    if (items.some(x => x.selected) || allSelected) return false;

    return true;
  };

  getSelectionParams = () => {
    const { items, allSelected } = this.state;

    // Get all relation ids that don't match the select all checkbox
    const relationIds = items ? items.filter(x => x.selected !== allSelected).map(x => x.id) : [];
    return {
      relationIds,
      selectAll: allSelected,
    };
  };

  sendEmailForSelectedRelations = email =>
    this.executeBatchAction({
      action: 'sendmail',
      parameters: email,
    });

  MarkComplianceTypeInapplicableForRelations = complianceType =>
    this.executeBatchAction({
      action: 'markinapplicable',
      parameters: complianceType,
    });

  createExcelReport = () => {
    const { intl } = this.props;
    const { currentSearchParams: searchParams } = this.state;

    const urlParams = {
      desc: searchParams.desc,
      sort: searchParams.sortKey,
      page: searchParams.page,
      take: searchParams.pageSize,
      culture: intl.locale,
      connectionId: SignalRService.getConnectionId(),
    };

    const statusFilters = Object.keys(searchParams.filter).reduce((obj, key) => {
      if (key !== 'amlRiskLevel' && key !== 'amlEndScore') {
        obj[key] = searchParams.filter[key].items;
      }
      return obj;
    }, {});

    const amlEndScore =
      searchParams.filter && searchParams.filter.amlEndScore
        ? searchParams.filter.amlEndScore.items || []
        : [];

    const bodyParams = {
      ...searchParams.relationFilters,
      statusFilters: {
        ...searchParams.relationFilters.statusFilters,
        ...statusFilters,
      },
      ...this.getSelectionParams(),
      amlEndScore,
    };

    this.setState({ creatingReport: true });

    return api.base
      .post(`/api/compliance/report`, bodyParams, {
        params: urlParams,
      })
      .then(() => {
        ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
      })
      .catch(error => {
        handleHttpError(intl)(error);
      })
      .finally(() => {
        this.setState({ creatingReport: false });
      });
  };

  executeBatchAction = ({ action, parameters }) => {
    if (this.isGlobalActionsDisabled()) return Promise.reject(new Error('invalid'));

    const { intl } = this.props;
    const { currentSearchParams: searchParams } = this.state;
    const { stateService } = this.props;

    let endpoint = '';
    let successMessage = '';
    let params = {};
    let reloadCompliancies = false;

    switch (action) {
      case 'sendmail':
        endpoint = 'CreateBatchMailJob';
        successMessage = intl.formatMessage(messages.SendEmailQueued);
        params = { ...parameters };
        break;
      case 'markinapplicable':
        endpoint = 'BatchToggleInapplicable';
        successMessage = parameters.inapplicable
          ? intl.formatMessage(messages.ComplianceMarkedAsInapplicable)
          : intl.formatMessage(messages.ComplianciesWillBeCalculatedInTheBackground);
        params = { ...parameters };
        reloadCompliancies = true;
        break;
      case 'createregistration':
        endpoint = 'CreateBatchRegistration';
        successMessage = intl.formatMessage(messages.NewRegistrationsQueued);
        // Registration fields are sent nested under "parameters" (matching BatchCreateRegistrationDto.Parameters
        // on the backend) instead of spread at the top level, since a flat spread would collide with the
        // compliance filters' own "type" field (compliance type) and leave batchDto.Parameters null server-side.
        params = { parameters };
        break;
      case 'tagsupdate':
        endpoint = 'RelationTagsUpdate';
        successMessage =
          parameters.action === 'add'
            ? intl.formatMessage(messages.LabelsAddedQueued)
            : intl.formatMessage(messages.LabelsRemovedQueued);
        params = { ...parameters };
        break;
      default:
        break;
    }

    const urlParams = {
      desc: searchParams.desc,
      sort: searchParams.sortKey,
      page: searchParams.page,
      take: searchParams.pageSize,
    };

    const statusFilters = Object.keys(searchParams.filter).reduce((obj, key) => {
      if (key !== 'amlRiskLevel' && key !== 'amlEndScore') {
        obj[key] = searchParams.filter[key].items;
      }
      return obj;
    }, {});

    const amlEndScore =
      searchParams.filter && searchParams.filter.amlEndScore
        ? searchParams.filter.amlEndScore.items || []
        : [];

    const bodyParams = {
      ...params,
      ...searchParams.relationFilters,
      statusFilters: {
        ...searchParams.relationFilters.statusFilters,
        ...statusFilters,
      },
      ...this.getSelectionParams(),
      amlEndScore,
    };

    const url = `/api/compliance/${endpoint}`;

    return api.base

      .post(url, bodyParams, { params: urlParams })
      .then(({ data }) => {
        this.setState(state => ({
          ...state,
          showSendMailModal: false,
          showMarkComplianceTypeAsInapplicableModal: false,
          showCreateRegistrationModal: false,
          showUpdateRelationTagsModal: false,
          initialSearchParams: reloadCompliancies
            ? {
                ...searchParams,
                // forces refresh of table
                random: new Date().getTime(),
              }
            : searchParams,
        }));

        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;

        ToastrService.info(successMessage, undefined, onClick);
      })
      .catch(handleHttpError(intl));
  };

  getSelectedRelationLanguages = () => {
    const { allSelected, items } = this.state;
    return new Promise((resolve, reject) => {
      if (allSelected) {
        const { currentSearchParams } = this.state;

        const urlParams = {
          desc: currentSearchParams.desc,
          sort: currentSearchParams.sortKey,
          page: currentSearchParams.page,
          take: currentSearchParams.pageSize,
        };

        const statusFilters = Object.keys(currentSearchParams.filter).reduce((obj, key) => {
          if (key !== 'amlRiskLevel' && key !== 'amlEndScore') {
            obj[key] = currentSearchParams.filter[key].items;
          }
          return obj;
        }, {});

        const amlEndScore =
          currentSearchParams.filter && currentSearchParams.filter.amlEndScore
            ? currentSearchParams.filter.amlEndScore.items || []
            : [];

        const bodyParams = {
          ...currentSearchParams.relationFilters,
          statusFilters: {
            ...currentSearchParams.relationFilters.statusFilters,
            ...statusFilters,
          },
          amlEndScore,
        };

        api.base
          .post(`/api/compliance/languages`, bodyParams, {
            params: {
              ...urlParams,
            },
          })
          .then(({ data }) => {
            resolve(data);
          })
          .catch(error => {
            reject(error);
          });
      } else {
        const languages = [...new Set(items.filter(x => x.selected).map(x => x.languageCode))]; // This makes sure it is a distinct list of language codes
        resolve(languages);
      }
    });
  };

  onSendEmail = () => {
    const { intl } = this.props;
    this.getSelectedRelationLanguages()
      .then(languages => {
        this.setState({
          showSendMailModal: true,
          relationLanguages: languages,
        });
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };
  onMarkComplianceTypeAsInapplicable = () => {
    this.setState({
      showMarkComplianceTypeAsInapplicableModal: true,
    });
  };

  renderRow = item => {
    const { complianceTypes } = this.state;
    const { intl } = this.props;

    return (
      <tbody className="hover" key={item.id}>
        <tr>
          <td>
            <Checkbox onClick={() => this.handleSelect(item)} value={item.selected} />
          </td>
          <td>
            <RelationName relation={item} />
          </td>
          <td>
            {item.relationComplianceRemark && (
              <PopoverStickOnHover
                trigger={['hover', 'focus']}
                placement="bottom"
                component={item.relationComplianceRemark}
                shouldUpdatePosition
                renderAsRichText={true}
              >
                <span className="margin10L explanation-icon pull-right">
                  <CommentIcon />
                </span>
              </PopoverStickOnHover>
            )}
          </td>
          <td>
            <label>
              <FormattedListValue list="amlrisklevel" id={item.amlEndScore} />
            </label>
          </td>
          {complianceTypes.map(complianceType => (
            <td className="text-center" key={complianceType}>
              <CompliancyStatusToggle
                id={item.id}
                item={item}
                compliancyStatus={
                  item.compliancies.find(x => x.complianceType === complianceType).complianceStatus
                }
                compliancyType={complianceType}
                onComplianceUpdated={this.handleUpdateComplianceStatus}
                onNavigateCompliance={this.iconClicked}
              />
            </td>
          ))}
          <td>
            <CanCreate on="InteractionsOwn">
              <a
                className="btn btn-xs btn-icon"
                title={intl.formatMessage(messages.SendEmail)}
                onClick={() =>
                  this.setState({ showSendSingleRelationMailModal: true, activeRelation: item })
                }
              >
                <FaEnvelope />
              </a>
            </CanCreate>
          </td>
        </tr>
      </tbody>
    );
  };

  render() {
    const {
      complianceKPIs,
      fullyCompliantKPI,
      initialSearchParams,
      items,
      allSelected,
      showSendMailModal,
      showMarkComplianceTypeAsInapplicableModal,
      relationLanguages,
      activeRelation,
      showSendSingleRelationMailModal,
      creatingReport,
      complianceTypes,
      currentSearchParams,
      showCreateRegistrationModal,
      showUpdateRelationTagsModal,
    } = this.state;
    const { intl } = this.props;
    const hasFilters =
      JSON.stringify(initialSearchParams.relationFilters) !==
        JSON.stringify(defaultRelationFilters) ||
      (currentSearchParams &&
        currentSearchParams.filter &&
        Object.keys(currentSearchParams.filter).length > 0);
    const hasData = items && items.length > 0;

    return (
      <div>
        {showSendMailModal && (
          <SendEmailModal
            intl={intl}
            show
            languages={relationLanguages}
            emailContext={13}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showSendMailModal: false,
              }))
            }
            onSave={emailModel => this.sendEmailForSelectedRelations(emailModel)}
          />
        )}
        {showMarkComplianceTypeAsInapplicableModal && (
          <MarkCompliancyInapplicableModal
            intl={intl}
            show
            onClose={() =>
              this.setState(state => ({
                ...state,
                showMarkComplianceTypeAsInapplicableModal: false,
              }))
            }
            onSave={complianceType =>
              this.MarkComplianceTypeInapplicableForRelations(complianceType)
            }
          />
        )}
        {showSendSingleRelationMailModal && (
          <SingleRelationMailModal
            intl={intl}
            show
            relationId={activeRelation.id}
            companyId={activeRelation.companyId}
            emailContext={13}
            onClose={() => this.setState({ showSendSingleRelationMailModal: false })}
            onSave={() => this.setState({ showSendSingleRelationMailModal: false })}
          />
        )}
        {showCreateRegistrationModal && (
          <BatchRegistrationModal
            intl={intl}
            show={showCreateRegistrationModal}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showCreateRegistrationModal: false,
              }))
            }
            onSave={batchRegistrationModel => this.createBatchRegistrations(batchRegistrationModel)}
            countSelectedRelations={this.getCountSelectedRelations()}
          />
        )}
        {showUpdateRelationTagsModal && (
          <UpdateRelationTagsModal
            intl={intl}
            show={showUpdateRelationTagsModal}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showUpdateRelationTagsModal: false,
              }))
            }
            onSave={data => this.updateRelationTags(data)}
          />
        )}
        <SimplePanel article="5434797066258" title={intl.formatMessage(messages.Compliance)}>
          <KPIs>
            {fullyCompliantKPI && (
              <KPI
                onClick={this.toggleNotFullyCompliantFilter}
                title={intl.formatMessage(messages.FullyCompliant)}
                subtitleClassName={classNames({
                  green: fullyCompliantKPI.percentage === 100,
                })}
                subtitle={<ComplianceKPISubtitle item={fullyCompliantKPI} />}
                isActive={!!initialSearchParams.relationFilters.onlyNotFullyCompliant}
              />
            )}
            {complianceKPIs.map(item => (
              <KPI
                key={item.title}
                onClick={() => this.filterByKpi(item.type)}
                title={item.title}
                subtitleClassName={classNames({
                  green: item.percentage === 100,
                })}
                subtitle={<ComplianceKPISubtitle item={item} />}
                isActive={Object.hasOwn(
                  initialSearchParams.relationFilters.statusFilters,
                  item.type,
                )}
              />
            ))}
          </KPIs>
          <div className="info-block container-fluid radius4B">
            <CompliancyFilters
              filters={initialSearchParams.relationFilters}
              onFilterChange={filters => this.filtersChanged(filters)}
              onResetFilters={() => this.resetFilters()}
              hasFilters={hasFilters}
              countSelectedRelations={this.getCountSelectionsFormatted()}
            />
          </div>
        </SimplePanel>
        <div className="row btn-row">
          <div className="col-md-6 pull-right content-right">
            <Button
              btnClassName="btn btn-xs btn-default margin10R"
              onClick={() => this.createExcelReport()}
              disabled={!hasData || creatingReport}
            >
              <span className="margin5R">
                {creatingReport && <i className="fa fa-spinner fa-spin" />}
                {!creatingReport && <FaFileExcel className="file-icon" />}
              </span>
              {intl.formatMessage(messages.ExportToExcel)}
            </Button>
            <CompliancyGlobalActionsButton
              onSendEmail={this.onSendEmail}
              onMarkComplianceTypeAsInapplicable={this.onMarkComplianceTypeAsInapplicable}
              disabled={this.isGlobalActionsDisabled()}
              onCreateRegistration={this.showCreateRegistrationModal}
              onUpdateTags={() => this.setState({ showUpdateRelationTagsModal: true })}
            />
          </div>
        </div>
        <ScrollableTable
          tableClassName="table-grid margin50B"
          loadItems={searchParams => this.loadItems(searchParams)}
          initialSearchParams={initialSearchParams}
          items={items}
          renderRow={item => this.renderRow(item)}
          noDataLabel={<FormattedMessage {...messages.NoComplianciesFound} />}
        >
          <ColumnHeader
            title={
              <Checkbox
                extraClass="margin2L"
                onClick={() => this.handleSelectAll()}
                value={allSelected}
              />
            }
          />
          <ColumnHeader title={intl.formatMessage(messages.Relation)} sortKey="displayName" />
          <ColumnHeader />
          <ColumnHeader
            title={intl.formatMessage(messages.AMLEndScore)}
            filter={{
              type: 'items',
              key: `amlEndScore`,
              items: [0, 1, 100],
              renderItem: item => formatListValue({ id: item, list: 'amlRiskLevel', intl }),
            }}
          />

          {complianceTypes.map(complianceType => (
            <ColumnHeader
              key={intl.formatMessage(listItemMsg('compliancetype', complianceType))}
              title={intl.formatMessage(listItemMsg('compliancetype', complianceType))}
              filter={{
                type: 'items',
                key: complianceType,
                items: [0, 1, 2, 3],
                renderItem: item => formatListValue({ id: item, list: 'compliancestatus', intl }),
              }}
              className="text-center compliance-column"
            />
          ))}
          <ColumnHeader title={intl.formatMessage(messages.Actions)} />
        </ScrollableTable>
      </div>
    );
  }

  static propTypes = {
    intl: intlShape.isRequired,
    stateService: PropTypes.instanceOf(StateService).isRequired,
  };

  static defaultProps = {};
}

export default injectStateService(injectIntl(CompliancyContainer));
